import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Activity, Calendar, ChevronLeft, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import {
  BODY_REGIONS,
  mapTermsToRegions,
  mapDatesToRegions,
  aggregateRegionTerms,
  buildRegionNarrative,
  getTier,
  MappedTerm,
  AggregatedFinding,
  TermCitation,
} from '../../data/bodyRegionMapping';
import { BodyMapSVG } from './BodyMapSVG';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { fromReviewCitation } from '../../types/citation';
import type { UnifiedCitation, DocumentViewRequest } from '../../types/citation';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { DerivedOutputBanner } from './DerivedOutputBanner';

interface BodyMapSubtabProps {
  caseId: string;
}

// Whole-body region IDs that don't map to a specific body area
const WHOLE_BODY_REGIONS = ['skin', 'systemic'];

// Friendly category labels
const CATEGORY_LABELS: Record<string, string> = {
  DIAGNOSIS: 'Diagnosis',
  MEDICATION: 'Medication',
  PROCEDURE: 'Procedure',
  SYMPTOM: 'Symptom',
  TEST: 'Investigation',
  VITAL_SIGN: 'Vital Sign',
  ANATOMY: 'Anatomy',
  CONDITION: 'Condition',
};

function formatCategory(raw: string): string {
  return CATEGORY_LABELS[raw?.toUpperCase()] || raw;
}

/** Abbreviate a filename to a short readable label, e.g. "original_03_hospital_admission.pdf" → "Hospital admission" */
function abbreviateDocName(filename: string): string {
  const name = filename
    .replace(/\.[^.]+$/, '')            // strip extension
    .replace(/^(original_?\d*_?)/i, '') // strip "original_03_" prefix
    .replace(/[_-]+/g, ' ')            // underscores/hyphens to spaces
    .replace(/\b(complete|full|final|copy|scan|scanned)\b/gi, '') // noise words
    .trim();
  // Capitalise first letter, keep rest lowercase
  if (!name) return filename;
  const words = name.split(/\s+/).filter(Boolean).slice(0, 4);
  return words.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

/** Truncate citation text to ~200 chars for better PDF search matching */
function truncateSearchText(text: string): string {
  if (!text || text.length <= 200) return text;
  // Take the first ~200 chars, break at a word boundary
  const truncated = text.slice(0, 200);
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > 100 ? truncated.slice(0, lastSpace) : truncated;
}

/** Deduplicate citations by documentId, keeping the one with the best page info */
function deduplicateCitationsByDoc(
  citations: TermCitation[],
  docNameMap: Map<string, string>,
): { docId: string; docLabel: string; pageNumber?: number; searchText?: string }[] {
  const seen = new Map<string, { pageNumber?: number; searchText?: string }>();
  for (const c of citations) {
    if (!c.documentId) continue;
    if (!seen.has(c.documentId)) {
      seen.set(c.documentId, {
        pageNumber: c.pageNumber,
        searchText: truncateSearchText(c.text),
      });
    }
  }
  return [...seen.entries()].map(([docId, info]) => ({
    docId,
    docLabel: abbreviateDocName(docNameMap.get(docId) || 'Source document'),
    pageNumber: info.pageNumber,
    searchText: info.searchText,
  }));
}

/** Format a date type into a readable label */
function formatDateType(type: string): string {
  return (type || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

export const BodyMapSubtab: React.FC<BodyMapSubtabProps> = ({ caseId }) => {
  const [view, setView] = useState<'front' | 'back'>('front');
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [keyDatesExpanded, setKeyDatesExpanded] = useState(true);
  const [showTier3, setShowTier3] = useState(false);
  const { openDocument } = useDocumentViewer();

  const handleOpenSource = useCallback((docId: string, docLabel: string, pageNumber?: number, searchText?: string) => {
    const request: DocumentViewRequest = {
      documentId: docId,
      documentName: docLabel,
      pageNumber,
      searchText: searchText || undefined,
    };
    openDocument(request);
  }, [openDocument]);

  // Read metadata from React Query cache
  const { data: metadata } = useQuery<any>(
    ['metadata', caseId],
    { enabled: false }
  );

  // Read documents from React Query cache for document name lookups
  const { data: documentsData } = useQuery<any[]>(
    ['documents', caseId],
    { enabled: false }
  );

  const documents = documentsData || [];

  // Build documentId → filename lookup
  const docNameMap = useMemo(() => {
    const map = new Map<string, string>();
    documents.forEach((doc: any) => {
      if (doc.documentId && doc.filename) {
        map.set(doc.documentId, doc.filename);
      }
    });
    return map;
  }, [documents]);

  const medicalTerms: MappedTerm[] = useMemo(() => {
    const terms = metadata?.medicalTerms || [];
    return terms.map((t: any) => ({
      term: t.term || '',
      category: t.category || 'CONDITION',
      frequency: t.frequency || 1,
      confidence: t.confidence || 0,
      citations: t.citations || [],
    }));
  }, [metadata]);

  const regionTermMap = useMemo(() => mapTermsToRegions(medicalTerms), [medicalTerms]);

  const regionDateMap = useMemo(() => {
    return mapDatesToRegions(metadata?.keyDates || []);
  }, [metadata]);

  const regionTermCounts = useMemo(() => {
    const counts = new Map<string, number>();
    regionTermMap.forEach((terms, regionId) => {
      if (regionId !== 'unmapped') {
        counts.set(regionId, terms.length);
      }
    });
    return counts;
  }, [regionTermMap]);

  // Summary statistics
  const { diagnosisCount, activeRegionCount, topRegionLabels, orientationSentence } = useMemo(() => {
    let active = 0;
    const regionDiagCounts: { label: string; diagCount: number; totalCount: number }[] = [];

    regionTermCounts.forEach((count, regionId) => {
      if (!WHOLE_BODY_REGIONS.includes(regionId) && count > 0) {
        active++;
        const terms = regionTermMap.get(regionId) || [];
        const diags = terms.filter(t => ['DIAGNOSIS', 'CONDITION'].includes(t.category?.toUpperCase()));
        const label = BODY_REGIONS.find(r => r.id === regionId)?.label || regionId;
        regionDiagCounts.push({ label, diagCount: diags.length, totalCount: count });
      }
    });

    // Sort by diagnosis count, then total count
    regionDiagCounts.sort((a, b) => b.diagCount - a.diagCount || b.totalCount - a.totalCount);

    const allDiags = medicalTerms.filter(t => ['DIAGNOSIS', 'CONDITION'].includes(t.category?.toUpperCase()));
    const topLabels = regionDiagCounts.slice(0, 3).map(r => r.label);

    let sentence = '';
    if (topLabels.length > 0) {
      const regionList = topLabels.length === 1
        ? topLabels[0]
        : topLabels.length === 2
          ? `${topLabels[0]} and ${topLabels[1]}`
          : `${topLabels.slice(0, -1).join(', ')}, and ${topLabels[topLabels.length - 1]}`;
      sentence = `This case primarily involves the ${regionList}, with ${allDiags.length} distinct ${allDiags.length === 1 ? 'diagnosis' : 'diagnoses'} across ${active} body ${active === 1 ? 'region' : 'regions'}.`;
    }

    return {
      diagnosisCount: allDiags.length,
      activeRegionCount: active,
      topRegionLabels: topLabels,
      orientationSentence: sentence,
    };
  }, [regionTermCounts, regionTermMap, medicalTerms]);

  // Whole-body terms (skin, systemic)
  const wholeBodyTerms = useMemo(() => {
    const terms: { regionId: string; label: string; terms: MappedTerm[] }[] = [];
    for (const regionId of WHOLE_BODY_REGIONS) {
      const regionTerms = regionTermMap.get(regionId) || [];
      if (regionTerms.length > 0) {
        const label = BODY_REGIONS.find(r => r.id === regionId)?.label || regionId;
        terms.push({ regionId, label, terms: regionTerms });
      }
    }
    return terms;
  }, [regionTermMap]);

  const activeRegion = hoveredRegion || selectedRegion;
  const activeRegionLabel = activeRegion
    ? BODY_REGIONS.find(r => r.id === activeRegion)?.label || activeRegion
    : null;
  const activeRegionTerms = activeRegion ? (regionTermMap.get(activeRegion) || []) : [];
  const activeRegionDates = activeRegion ? (regionDateMap.get(activeRegion) || []) : [];
  const unmappedTerms = regionTermMap.get('unmapped') || [];

  // Aggregated findings for the active region
  const activeAggregated = useMemo(
    () => aggregateRegionTerms(activeRegionTerms),
    [activeRegionTerms]
  );

  // Ranked regions for default detail panel — sort by diagnosis count first
  const rankedRegions = useMemo(() => {
    return [...regionTermCounts.entries()]
      .filter(([regionId, count]) => count > 0 && !WHOLE_BODY_REGIONS.includes(regionId))
      .map(([regionId, count]) => {
        const terms = regionTermMap.get(regionId) || [];
        const diagCount = terms.filter(t => ['DIAGNOSIS', 'CONDITION'].includes(t.category?.toUpperCase())).length;
        return { regionId, count, diagCount };
      })
      .sort((a, b) => b.diagCount - a.diagCount || b.count - a.count);
  }, [regionTermCounts, regionTermMap]);

  /** Render a single aggregated finding row */
  const renderFindingRow = (finding: AggregatedFinding, idx: number) => {
    const sources = deduplicateCitationsByDoc(finding.citations, docNameMap);

    return (
      <div key={idx} className="py-2 first:pt-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900 dark:text-medical-100 truncate min-w-0">
            {finding.label}
          </span>
          <span className="text-[10px] px-1.5 py-px bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded whitespace-nowrap flex-shrink-0">
            {formatCategory(finding.category)}
          </span>
        </div>
        {sources.length > 0 && (
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            <FileText className="w-3 h-3 text-gray-400 dark:text-medical-500 flex-shrink-0" />
            {sources.map((src, sIdx) => (
              <span key={src.docId} className="inline-flex items-center">
                <button
                  onClick={() => handleOpenSource(src.docId, src.docLabel, src.pageNumber, src.searchText)}
                  className="text-[11px] text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 hover:underline transition-colors"
                  title={`Open ${docNameMap.get(src.docId) || src.docLabel}${src.pageNumber ? ` — page ${src.pageNumber}` : ''}`}
                >
                  {src.docLabel}{src.pageNumber ? `, p.${src.pageNumber}` : ''}
                </button>
                {sIdx < sources.length - 1 && (
                  <span className="text-gray-300 dark:text-medical-600 mx-1">|</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  /** Render the clinical detail panel for a selected region */
  const renderRegionDetail = () => {
    if (!activeRegion || activeRegionTerms.length === 0) return null;

    const tier1and2 = activeAggregated.filter(f => getTier(f.category) <= 2);
    const tier3 = activeAggregated.filter(f => getTier(f.category) === 3);
    const narrative = buildRegionNarrative(activeAggregated);

    return (
      <>
        {/* Header with back button */}
        <div className="flex items-center gap-2 mb-3">
          {selectedRegion && (
            <button
              onClick={() => setSelectedRegion(null)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-medical-700 text-gray-400 dark:text-medical-500"
              title="Back to all regions"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <h4 className="text-lg font-semibold text-gray-900 dark:text-medical-100">
            {activeRegionLabel}
          </h4>
        </div>

        {/* Narrative summary */}
        <p className="text-sm text-gray-700 dark:text-medical-300 mb-4 leading-relaxed">
          {narrative}
        </p>

        {/* Related dates — collapsible with clickable citations */}
        {activeRegionDates.length > 0 && (
          <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg">
            <button
              onClick={() => setKeyDatesExpanded(prev => !prev)}
              className="w-full flex items-center gap-1.5 p-2.5 text-xs font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
            >
              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
              Key Dates
              <span className="text-amber-600 dark:text-amber-400 font-normal">({activeRegionDates.length})</span>
              {keyDatesExpanded ? (
                <ChevronDown className="w-3.5 h-3.5 ml-auto" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 ml-auto" />
              )}
            </button>
            {keyDatesExpanded && (
              <div className="px-2.5 pb-2.5 space-y-2">
                {activeRegionDates.map((kd, idx) => {
                  const citations: UnifiedCitation[] = (kd.citations || []).map((c, cIdx) => {
                    const docName = docNameMap.get(c.documentId) || 'Source document';
                    return fromReviewCitation({ ...c, relevanceScore: c.relevanceScore || 0 }, docName);
                  });

                  return (
                    <div key={idx} className="text-xs">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="font-semibold text-amber-900 dark:text-amber-200 whitespace-nowrap tabular-nums">
                          {kd.date}
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-200/60 dark:bg-amber-800/40 text-amber-800 dark:text-amber-300">
                          {formatDateType(kd.type)}
                        </span>
                      </div>
                      <div className="text-amber-700 dark:text-amber-400 leading-relaxed prose prose-sm dark:prose-invert max-w-none [&_*]:text-amber-700 dark:[&_*]:text-amber-400">
                        <MarkdownWithCitations
                          content={kd.context}
                          citations={citations}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tier 1 & 2 findings — always visible */}
        {tier1and2.length > 0 && (
          <div className="divide-y divide-gray-100 dark:divide-medical-700">
            {tier1and2.map(renderFindingRow)}
          </div>
        )}

        {/* Tier 3 findings — collapsed behind toggle */}
        {tier3.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowTier3(prev => !prev)}
              className="text-xs text-gray-500 dark:text-medical-400 hover:text-gray-700 dark:hover:text-medical-300 transition-colors"
            >
              {showTier3 ? 'Hide' : 'Show'} {tier3.length} more {tier3.length === 1 ? 'finding' : 'findings'} (medications, investigations, etc.)
            </button>
            {showTier3 && (
              <div className="mt-2 divide-y divide-gray-100 dark:divide-medical-700 opacity-75">
                {tier3.map(renderFindingRow)}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  /** Render the default "All Regions" overview with summaries */
  const renderAllRegions = () => {
    if (rankedRegions.length === 0) return null;

    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-medical-300 mb-3">
          All Regions
        </h4>
        <div className="space-y-2">
          {rankedRegions.map(({ regionId, diagCount }) => {
            const label = BODY_REGIONS.find(r => r.id === regionId)?.label || regionId;
            const terms = regionTermMap.get(regionId) || [];
            const dates = regionDateMap.get(regionId) || [];
            const aggregated = aggregateRegionTerms(terms);

            // Show top aggregated diagnoses/conditions as preview
            const topFindings = aggregated
              .filter(f => getTier(f.category) <= 2)
              .slice(0, 3)
              .map(f => f.label);

            return (
              <button
                key={regionId}
                onClick={() => setSelectedRegion(regionId)}
                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-medical-600"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-medical-100">{label}</span>
                  {diagCount > 0 && (
                    <span className="text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
                      {diagCount} {diagCount === 1 ? 'diagnosis' : 'diagnoses'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-medical-400 leading-relaxed">
                  {topFindings.join(', ')}
                  {dates.length > 0 && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400">
                      | {dates.length} key {dates.length === 1 ? 'date' : 'dates'}
                    </span>
                  )}
                </p>
              </button>
            );
          })}

          {/* Whole-body conditions inline */}
          {wholeBodyTerms.length > 0 && (
            <>
              <div className="border-t border-gray-200 dark:border-medical-700 mt-2 pt-3">
                <h5 className="text-xs font-semibold text-gray-500 dark:text-medical-400 mb-2 uppercase tracking-wide">
                  Whole-body
                </h5>
                {wholeBodyTerms.map(({ regionId, label, terms }) => (
                  <button
                    key={regionId}
                    onClick={() => setSelectedRegion(regionId)}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-medical-600"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-medical-100">{label}</span>
                      <span className="text-xs text-gray-500 dark:text-medical-400">
                        {terms.length} {terms.length === 1 ? 'finding' : 'findings'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (medicalTerms.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <div className="text-center">
          <Activity className="w-12 h-12 text-gray-300 dark:text-medical-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-medical-100 mb-2">No Medical Terms Available</h3>
          <p className="text-gray-600 dark:text-medical-400">
            The uploaded evidence did not contain sufficient information to identify medical terms that can be mapped to body regions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="body map"
        evaluatePrompt="I have corrected some chronology events since the body map was generated. Review the edited events and assess whether the corrections affect any injury locations, anatomical findings, or regional clinical details shown on the body map."
      />

      <div className="mb-4 mt-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Body Map</h3>
        {orientationSentence && (
          <p className="text-sm text-gray-700 dark:text-medical-300 leading-relaxed">
            {orientationSentence}
          </p>
        )}
      </div>

      {/* Muted raw stats */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-400 dark:text-medical-500">
        <span>{diagnosisCount} {diagnosisCount === 1 ? 'diagnosis' : 'diagnoses'}</span>
        <span>{activeRegionCount} {activeRegionCount === 1 ? 'region' : 'regions'} affected</span>
        {unmappedTerms.length > 0 && (
          <span>({unmappedTerms.length} terms not mapped to a body region)</span>
        )}
      </div>

      {/* Main Layout — body visual compact on left, detail panel takes majority */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Body SVG + Toggle + Legend */}
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-4">
          {/* Front/Back toggle */}
          <div className="flex justify-center mb-3">
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-medical-700 p-0.5">
              <button
                onClick={() => setView('front')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  view === 'front'
                    ? 'bg-white dark:bg-medical-600 text-gray-900 dark:text-medical-100 shadow-sm'
                    : 'text-gray-600 dark:text-medical-400 hover:text-gray-900 dark:hover:text-medical-200'
                }`}
              >
                Front
              </button>
              <button
                onClick={() => setView('back')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  view === 'back'
                    ? 'bg-white dark:bg-medical-600 text-gray-900 dark:text-medical-100 shadow-sm'
                    : 'text-gray-600 dark:text-medical-400 hover:text-gray-900 dark:hover:text-medical-200'
                }`}
              >
                Back
              </button>
            </div>
          </div>

          <BodyMapSVG
            view={view}
            regionTermCounts={regionTermCounts}
            selectedRegion={selectedRegion}
            onRegionClick={setSelectedRegion}
            onRegionHover={setHoveredRegion}
          />

          {/* Color Legend */}
          <div className="mt-3 flex items-center justify-center gap-2 text-[10px] text-gray-500 dark:text-medical-400">
            <span>Fewer</span>
            <div className="w-20 h-2 rounded-full bg-gradient-to-r from-red-200 via-red-400 to-red-700 dark:from-red-900 dark:via-red-500 dark:to-red-300 border border-gray-200 dark:border-medical-600" />
            <span>More</span>
          </div>
        </div>

        {/* Region Detail Panel */}
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-6 max-h-[600px] overflow-y-auto">
          {activeRegion && activeRegionTerms.length > 0 ? (
            renderRegionDetail()
          ) : activeRegion ? (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-medical-400">
              <div className="text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-medical-600" />
                <p className="text-sm">No medical findings mapped to this region</p>
              </div>
            </div>
          ) : rankedRegions.length > 0 ? (
            renderAllRegions()
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-medical-400">
              <div className="text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-medical-600" />
                <p className="text-sm">Click a body region to see clinical details</p>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};
